import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIYOrderModal = ({ isOpen, onClose }) => {
  const [quantities, setQuantities] = useState({
    aluminumTrack: 0,
    goveePro: 0,
    illumidelLights: 0,
    screws: 0,
    controlBox240: 0,
    controlBox300: 0,
    powerBox240: 0,
    powerBox300: 0
  });

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const parts = [
    { 
      id: 'aluminumTrack', 
      name: '6ft Aluminum Track', 
      price: 29.99,
      unit: 'per track'
    },
    { 
      id: 'goveePro', 
      name: 'Govee Pro 6ft Aluminum Track', 
      price: 29.99,
      unit: 'per track'
    },
    { 
      id: 'illumidelLights', 
      name: 'IllumiDEL Lights', 
      price: 11.99,
      unit: 'per foot',
      note: 'Orders include accessories'
    },
    { 
      id: 'screws', 
      name: 'Colour Matched Screws', 
      price: 11.49,
      unit: 'per 100 screws'
    },
    { 
      id: 'controlBox240', 
      name: '240ft Control Box', 
      price: 349,
      unit: 'per unit'
    },
    { 
      id: 'controlBox300', 
      name: '300ft Control Box', 
      price: 599,
      unit: 'per unit'
    },
    { 
      id: 'powerBox240', 
      name: '240ft Additional Power Box', 
      price: 299,
      unit: 'per unit'
    },
    { 
      id: 'powerBox300', 
      name: '300ft Additional Power Box', 
      price: 449,
      unit: 'per unit'
    }
  ];

  const updateQuantity = (id, value) => {
    setQuantities(prev => ({
      ...prev,
      [id]: Math.max(0, value)
    }));
  };

  const calculateSubtotal = () => {
    let subtotal = 0;
    parts.forEach(part => {
      subtotal += quantities[part.id] * part.price;
    });
    return subtotal;
  };

  const calculateTaxes = () => {
    const subtotal = calculateSubtotal();
    const gst = subtotal * 0.05; // 5% GST
    const pst = subtotal * 0.06; // 6% PST
    return { gst, pst, total: gst + pst };
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const taxes = calculateTaxes();
    return (subtotal + taxes.total).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const hasItems = Object.values(quantities).some(qty => qty > 0);
    if (!hasItems) {
      toast.error('Please select at least one item');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create order payload
      const orderPayload = {
        customer: customerInfo,
        items: parts.filter(part => quantities[part.id] > 0).map(part => ({
          name: part.name,
          quantity: quantities[part.id],
          price: part.price,
          subtotal: parseFloat((quantities[part.id] * part.price).toFixed(2))
        })),
        subtotal: calculateSubtotal(),
        gst: calculateTaxes().gst,
        pst: calculateTaxes().pst,
        total: parseFloat(calculateTotal())
      };

      // Send to backend
      const response = await axios.post(`${API}/diy-orders`, orderPayload);
      
      toast.success('Order submitted successfully! We\'ll contact you shortly with next steps.');
      
      // Reset form
      setQuantities({
        aluminumTrack: 0,
        goveePro: 0,
        illumidelLights: 0,
        screws: 0,
        controlBox240: 0,
        controlBox300: 0,
        powerBox240: 0,
        powerBox300: 0
      });
      setCustomerInfo({
        name: '',
        email: '',
        phone: '',
        address: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting DIY order:', error);
      toast.error('Failed to submit order. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Order DIY Kits</h2>
          <button onClick={onClose} className="text-white hover:text-blue-400 transition-colors">
            <X size={28} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Information Banner */}
          <div className="mb-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="bg-blue-500 rounded-full p-2 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">What Happens Next?</h3>
                <p className="text-gray-700 mb-3">
                  After this order form is filled in, we will contact you for further details of the project to make sure you have all that you need for an easy installation including:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold mt-1">✓</span>
                    <span>Colour matched tracks correctly sized for your project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold mt-1">✓</span>
                    <span>Detailed installation instructions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold mt-1">✓</span>
                    <span>Complete list of tools required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold mt-1">✓</span>
                    <span>Access to video installation manuals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Parts List */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Select Parts</h3>
            <div className="space-y-4">
              {parts.map((part) => (
                <Card key={part.id} className="border-2 border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{part.name}</h4>
                        <p className="text-blue-600 font-bold">${part.price} {part.unit}</p>
                        {part.note && (
                          <p className="text-sm text-gray-600 italic mt-1">{part.note}</p>
                        )}
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => updateQuantity(part.id, quantities[part.id] - 1)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                        >
                          <Minus size={16} />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={quantities[part.id]}
                          onChange={(e) => updateQuantity(part.id, parseInt(e.target.value) || 0)}
                          className="w-16 text-center border-2 border-gray-300 rounded-lg py-1 font-semibold"
                        />
                        <button
                          type="button"
                          onClick={() => updateQuantity(part.id, quantities[part.id] + 1)}
                          className="w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition-colors"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Total */}
            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-gray-700">
                  <span className="font-medium">Subtotal:</span>
                  <span className="font-semibold">${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span className="font-medium">GST (5%):</span>
                  <span className="font-semibold">${calculateTaxes().gst.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span className="font-medium">PST (6%):</span>
                  <span className="font-semibold">${calculateTaxes().pst.toFixed(2)}</span>
                </div>
                <div className="border-t-2 border-blue-200 pt-2 mt-2"></div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">Total:</span>
                  <span className="text-3xl font-bold text-blue-600">${calculateTotal()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <form onSubmit={handleSubmit}>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Your Information</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-gray-700 font-semibold">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 font-semibold">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700 font-semibold">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="address" className="text-gray-700 font-semibold">Shipping Address</Label>
                <Input
                  id="address"
                  type="text"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                  placeholder="123 Main Street, City, State ZIP"
                  className="mt-2"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg py-6 mt-6"
            >
              Submit Order
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DIYOrderModal;
